import { ArrowLeft, Pencil, Trash2 } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import type { ApiError } from '@/core/api';
import { useEmployee } from '@/features/hrm/employees/queries';
import { employeeName } from '@/features/hrm/employees/types';
import { OnboardingChecklist } from '../components/OnboardingChecklist';
import {
  onboardingKindLabel,
  onboardingStatusLabel,
  onboardingStatusVariant,
} from '../constants';
import { useDeleteOnboarding, useOnboarding } from '../queries';

export function OnboardingDetailPage() {
  const { publicId = '' } = useParams();
  const navigate = useNavigate();

  const { data: onboarding, isPending, isError, error } = useOnboarding(publicId);
  const employee = useEmployee(onboarding?.employee ?? -1);
  const del = useDeleteOnboarding();

  const label = employee.data ? employeeName(employee.data) : `Employee #${onboarding?.employee ?? ''}`;

  async function handleDelete() {
    if (!onboarding) return;
    await del.mutateAsync(onboarding.id);
    navigate('/hrm/onboarding');
  }

  if (isPending) {
    return (
      <div className="max-w-3xl space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/hrm/onboarding')}>
          <ArrowLeft /> Back
        </Button>
        <p className="text-destructive">{(error as unknown as ApiError).message}</p>
      </div>
    );
  }

  const fmtDate = (d: string | null) => (d ? new Date(d).toLocaleDateString() : '—');

  return (
    <div className="max-w-3xl space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/hrm/onboarding')}>
            <ArrowLeft />
          </Button>
          <h1 className="text-2xl font-semibold tracking-tight">{label}</h1>
          <Badge variant={onboardingStatusVariant(onboarding.status)}>
            {onboardingStatusLabel(onboarding.status)}
          </Badge>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => navigate(`/hrm/onboarding/${onboarding.public_id}/edit`)}
          >
            <Pencil /> Edit
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive">
                <Trash2 /> Delete
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete this checklist?</AlertDialogTitle>
                <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleDelete} disabled={del.isPending}>
                  {del.isPending ? 'Deleting…' : 'Delete'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Details</CardTitle>
        </CardHeader>
        <CardContent className="text-muted-foreground grid gap-4 text-sm sm:grid-cols-3">
          <Field label="Employee">{label}</Field>
          <Field label="Kind">{onboardingKindLabel(onboarding.kind)}</Field>
          <Field label="Status">{onboardingStatusLabel(onboarding.status)}</Field>
          <Field label="Started">{fmtDate(onboarding.started_on)}</Field>
          <Field label="Completed">{fmtDate(onboarding.completed_on)}</Field>
        </CardContent>
      </Card>

      <OnboardingChecklist onboarding={onboarding} />
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs font-medium uppercase">{label}</p>
      <div className="text-foreground mt-1">{children}</div>
    </div>
  );
}
